import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { followupService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  PhoneIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { format, isPast, isToday, isTomorrow } from 'date-fns';
import { Spinner } from '../components/Spinner';

export const Followups = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    callStatus: '',
    activityType: '',
    page: 1,
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: followupsData, isLoading } = useQuery(
    ['followups', filters],
    () => followupService.getFollowups(filters),
    {
      keepPreviousData: true,
    }
  );

  const completeFollowupMutation = useMutation(
    ({ id, completionData }) => followupService.completeFollowup(id, completionData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('followups');
        queryClient.invalidateQueries('followupStats');
      },
    }
  );

  const handleComplete = async (id) => {
    const outcome = window.prompt('Follow-up outcome (optional):');
    const notes = window.prompt('Follow-up notes (optional):');
    
    await completeFollowupMutation.mutateAsync({
      id,
      completionData: {
        outcome: outcome || 'completed',
        notes: notes || '',
        duration: 15, // Default 15 minutes
      },
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'badge-warning', text: 'Pending', icon: ClockIcon },
      completed: { color: 'badge-success', text: 'Completed', icon: CheckCircleIcon },
      missed: { color: 'badge-danger', text: 'Missed', icon: ExclamationTriangleIcon },
      rescheduled: { color: 'badge-secondary', text: 'Rescheduled', icon: ClockIcon },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color} flex items-center`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getCallStatusBadge = (callStatus) => {
    const statusConfig = {
      call_back: { color: 'badge-secondary', text: 'Call Back' },
      ask_to_call_later: { color: 'badge-secondary', text: 'Ask to Call Later' },
      ringing_not_responding: { color: 'badge-warning', text: 'Ringing' },
      not_reachable: { color: 'badge-danger', text: 'Not Reachable' },
      switch_off: { color: 'badge-danger', text: 'Switch Off' },
      connected: { color: 'badge-success', text: 'Connected' },
      voicemail: { color: 'badge-secondary', text: 'Voicemail' },
    };
    const config = statusConfig[callStatus] || statusConfig.call_back;
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  const getActivityTypeBadge = (activityType) => {
    const typeConfig = {
      face_to_face: { color: 'badge-primary', text: 'Face to Face' },
      home_visit: { color: 'badge-primary', text: 'Home Visit' },
      showroom_visit: { color: 'badge-primary', text: 'Showroom Visit' },
      test_drive_schedule: { color: 'badge-success', text: 'Test Drive' },
      test_drive_completed: { color: 'badge-success', text: 'Test Drive Done' },
      phone_call: { color: 'badge-secondary', text: 'Phone Call' },
      email: { color: 'badge-secondary', text: 'Email' },
      whatsapp: { color: 'badge-secondary', text: 'WhatsApp' },
      other: { color: 'badge-secondary', text: 'Other' },
    };
    const config = typeConfig[activityType] || typeConfig.phone_call;
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  const getFollowupDateDisplay = (date) => {
    const followupDate = new Date(date);
    if (isToday(followupDate)) {
      return 'Today';
    } else if (isTomorrow(followupDate)) {
      return 'Tomorrow';
    } else if (isPast(followupDate)) {
      return 'Overdue';
    } else {
      return format(followupDate, 'MMM dd, yyyy');
    }
  };

  const getFollowupDateColor = (date) => {
    const followupDate = new Date(date);
    if (isToday(followupDate)) {
      return 'text-warning-600';
    } else if (isTomorrow(followupDate)) {
      return 'text-primary-600';
    } else if (isPast(followupDate)) {
      return 'text-danger-600';
    } else {
      return 'text-gray-600';
    }
  };

  const followups = followupsData?.data?.followups || [];
  const pagination = followupsData?.data?.pagination || {};

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Follow-ups</h1>
            <p className="mt-2 text-gray-600">Manage and track customer follow-ups</p>
          </div>
          {hasPermission('followups', 'create') && (
            <Link
              to="/followups/new"
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Schedule Follow-up
            </Link>
          )}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: '', page: 1 }))}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                !filters.status
                  ? 'bg-primary-100 text-primary-800'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'pending', page: 1 }))}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.status === 'pending'
                  ? 'bg-warning-100 text-warning-800'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, overdue: 'true', page: 1 }))}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.overdue === 'true'
                  ? 'bg-danger-100 text-danger-800'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Overdue
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, today: 'true', page: 1 }))}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.today === 'true'
                  ? 'bg-primary-100 text-primary-800'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'completed', page: 1 }))}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.status === 'completed'
                  ? 'bg-success-100 text-success-800'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-4 sm:px-6 sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search follow-ups..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline-primary"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="missed">Missed</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Call Status
                </label>
                <select
                  value={filters.callStatus}
                  onChange={(e) => handleFilterChange('callStatus', e.target.value)}
                  className="input"
                >
                  <option value="">All Call Statuses</option>
                  <option value="call_back">Call Back</option>
                  <option value="ask_to_call_later">Ask to Call Later</option>
                  <option value="ringing_not_responding">Ringing</option>
                  <option value="not_reachable">Not Reachable</option>
                  <option value="switch_off">Switch Off</option>
                  <option value="connected">Connected</option>
                  <option value="voicemail">Voicemail</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type
                </label>
                <select
                  value={filters.activityType}
                  onChange={(e) => handleFilterChange('activityType', e.target.value)}
                  className="input"
                >
                  <option value="">All Activity Types</option>
                  <option value="face_to_face">Face to Face</option>
                  <option value="home_visit">Home Visit</option>
                  <option value="showroom_visit">Showroom Visit</option>
                  <option value="test_drive_schedule">Test Drive Schedule</option>
                  <option value="test_drive_completed">Test Drive Completed</option>
                  <option value="phone_call">Phone Call</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Follow-ups Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Spinner size="lg" />
            <p className="mt-2 text-gray-500">Loading follow-ups...</p>
          </div>
        ) : followups.length === 0 ? (
          <div className="p-8 text-center">
            <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No follow-ups found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.status || filters.callStatus || filters.activityType
                ? 'Try adjusting your filters'
                : 'Get started by scheduling a new follow-up'}
            </p>
            {hasPermission('followups', 'create') && (
              <div className="mt-6">
                <Link to="/followups/new" className="btn-primary">
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Schedule Follow-up
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Lead</th>
                    <th className="table-header-cell">Follow-up Date</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Activity</th>
                    <th className="table-header-cell">Assigned To</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {followups.map((followup) => (
                    <tr key={followup._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {followup.leadId.customerInfo.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {followup.leadId.leadInfo.brand} {followup.leadId.leadInfo.model}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div>
                          <div className={`text-sm font-medium ${getFollowupDateColor(followup.followupInfo.nextFollowupDate)}`}>
                            {getFollowupDateDisplay(followup.followupInfo.nextFollowupDate)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {format(new Date(followup.followupInfo.nextFollowupDate), 'MMM dd, yyyy HH:mm')}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">{getStatusBadge(followup.status)}</td>
                      <td className="table-cell">
                        <div className="space-y-1">
                          {getActivityTypeBadge(followup.activityType)}
                          {followup.callStatus && getCallStatusBadge(followup.callStatus)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {followup.followupInfo.updatedBy.firstName} {followup.followupInfo.updatedBy.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/leads/${followup.leadId._id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Lead
                          </Link>
                          {followup.status === 'pending' && hasPermission('followups', 'update') && (
                            <button
                              onClick={() => handleComplete(followup._id)}
                              className="text-success-600 hover:text-success-900"
                              disabled={completeFollowupMutation.isLoading}
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current >= pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(pagination.current - 1) * 10 + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.current * 10, pagination.total)}
                      </span>{' '}
                      of <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.current - 1)}
                        disabled={pagination.current <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.current
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(pagination.current + 1)}
                        disabled={pagination.current >= pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
